/**
 * 教材管理页
 * ------------------------------------------------------------
 * 展示：教材列表 → 单元列表 → 单元内单词/句子/语法/题目
 * 内容全部来自 dataSource（文件 + 导入数据合并），页面不含任何硬编码教材内容。
 */
import { state, saveSelection } from '../core/store.js';
import { listTextbooks, listUnits, getUnitContent, unitStats, invalidateCache } from '../services/dataSource.js';
import { card, emptyState, tag, progressBar, openModal, confirmDialog, closeModal, masteryMatrix } from '../components/ui.js';
import { WORD_LEVELS } from '../engines/generators/wordLevels.js';
import { SENTENCE_LEVELS } from '../engines/generators/sentenceLevels.js';
import { masteryOf } from '../services/records.js';
import { navigate } from '../core/router.js';
import { escapeHtml, autoSpeak, toast } from '../utils/index.js';
import { playWord, playSentenceEn } from '../services/audioService.js';
import { deleteUnitContent, packToDraft, saveDraft } from '../services/importer.js';
import { emit, EVENTS } from '../core/events.js';
import { loadLibrary, markConfirmed } from '../services/library.js';
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';
import { makeQuestion } from '../core/models.js';

export default async function renderTextbooks(mount, params = {}) {
  const books = await listTextbooks();

  if (!books.length) {
    mount.innerHTML = `<div class="page-head"><h1>📚 教材管理</h1></div>
      ${emptyState('📭', '还没有任何教材', '内置教材文件位于 data/textbooks/，也可以通过导入中心添加',
        '<a class="btn btn-primary" href="#/import">去导入教材</a>')}`;
    return;
  }

  const activeId = params.textbookId || state.selection.textbookId || books[0].id;
  const book = books.find(b => b.id === activeId) || books[0];
  const units = await listUnits(book.id);
  const activeUnitId = params.unit || state.selection.unitId || units[0]?.id || '';

  mount.innerHTML = `
    <div class="page-head">
      <h1>📚 教材管理</h1>
      <p class="lead">教材内容与程序代码完全分离：新增教材只需放数据文件或走导入流程，不用改任何代码。</p>
    </div>

    <div id="library-block"></div>

    <div class="split">
      <div class="col" style="gap:16px">
        ${card(
          `<div class="book-tabs">
            ${books.map(b => `
              <button class="book-tab ${b.id === book.id ? 'on' : ''}" data-book="${b.id}" type="button">
                <span class="book-name">${escapeHtml(b.name)}</span>
                <span class="book-meta">${b.grade ? b.grade + ' 年级' : '未设年级'} · ${escapeHtml(b.term || '上')}册 ${b.source === 'store' ? '· 已导入' : ''}</span>
              </button>`).join('')}
          </div>`,
          { title: '教材列表', extra: tag(`${books.length} 本`, 'blue') }
        )}

        ${card(
          units.length
            ? `<div class="unit-list">
                ${units.map(u => `
                  <button class="unit-row ${u.id === activeUnitId ? 'on' : ''}" data-unit="${u.id}" type="button">
                    <span class="unit-no">${u.no}</span>
                    <span class="grow">
                      <strong>${escapeHtml(u.title || '未命名单元')}</strong>
                      <small class="muted" data-stats="${u.id}">统计中…</small>
                    </span>
                    <span class="tag ${u.source === 'store' ? 'green' : ''}">${u.source === 'store' ? '已导入' : '内置'}</span>
                  </button>`).join('')}
              </div>
              <div class="row mt-8" style="gap:8px">
                <a class="btn btn-sm btn-ghost" href="#/import">＋ 导入新单元</a>
              </div>`
            : emptyState('🗂️', '这本教材还没有单元', '通过导入中心添加单元',
                '<a class="btn btn-primary" href="#/import">去导入</a>'),
          { title: `${escapeHtml(book.name)} · 单元`, extra: tag(`${units.length} 个单元`, '') }
        )}

        <div id="unit-detail"></div>
      </div>

      <div class="col" style="gap:16px">
        ${card(`
          <div class="info-list">
            <div><span class="muted">书名</span><b>${escapeHtml(book.name)}</b></div>
            <div><span class="muted">版本</span><b>${escapeHtml(book.version || '—')}</b></div>
            <div><span class="muted">年级</span><b>${book.grade ? book.grade + ' 年级' : '—'}</b></div>
            <div><span class="muted">学期</span><b>${escapeHtml(book.term || '—')}</b></div>
            <div><span class="muted">来源</span><b>${book.source === 'store' ? '导入入库' : '内置数据文件'}</b></div>
          </div>
          ${book.note ? `<p class="muted mt-8" style="font-size:12px">${escapeHtml(book.note)}</p>` : ''}`,
          { title: '教材信息' })}

        ${card(`
          <p class="muted" style="font-size:12.5px">
            当前单元的数据文件路径（供开发者参考）：
          </p>
          <code class="mono block">data/textbooks/${escapeHtml(book.dir || book.id)}/units/&lt;unit&gt;.json</code>
          <p class="muted mt-8" style="font-size:12px">
            直接编辑这些 JSON 文件，刷新页面即可生效；也可以在导入中心把内置内容「复制到本地库」后再编辑。
          </p>`,
          { title: '数据位置' })}
      </div>
    </div>`;

  /* ---------- 资料库（已导入的 PDF） ---------- */
  await renderLibrary(mount);

  /* ---------- 单元统计（异步填充） ---------- */
  (async () => {
    for (const u of units) {
      const el = mount.querySelector(`[data-stats="${u.id}"]`);
      if (!el) continue;
      try {
        const s = await unitStats(book.id, u.id);
        el.textContent = `${s.words} 词 · ${s.sentences} 句 · ${s.texts || 0} 课文 · ${s.grammar} 语法点 · ${s.questions} 题`;
      } catch {
        el.textContent = '内容未加载';
      }
    }
  })();

  /* ---------- 单元详情 ---------- */
  if (activeUnitId) await renderUnitDetail(mount, book, units.find(u => u.id === activeUnitId) || units[0]);

  /* ---------- 交互 ---------- */
  mount.querySelectorAll('[data-book]').forEach(btn => {
    btn.addEventListener('click', () => {
      navigate('#/textbooks', { textbookId: btn.dataset.book });
    });
  });
  mount.querySelectorAll('[data-unit]').forEach(btn => {
    btn.addEventListener('click', async () => {
      await saveSelection({ textbookId: book.id, unitId: btn.dataset.unit });
      const box = document.getElementById('unit-detail');
      box.innerHTML = '<div class="page-loading">加载单元内容…</div>';
      mount.querySelectorAll('[data-unit]').forEach(b => b.classList.toggle('on', b === btn));
      await renderUnitDetail(mount, book, units.find(u => u.id === btn.dataset.unit));
    });
  });
}

/* ------------------------------ 单元详情 ------------------------------ */

async function renderUnitDetail(mount, book, unit) {
  const box = document.getElementById('unit-detail');
  if (!box || !unit) return;

  const content = await getUnitContent(book.id, unit.id);
  const masteryRows = await masteryOf(unit.id);

  const wordRows = content.words.map(w => {
    const row = masteryRows.find(m => m.refId === w.id && m.refType === 'word');
    return { label: w.en, sub: w.zh, levels: row?.levels || {} };
  });
  const sentRows = content.sentences.map(s => {
    const row = masteryRows.find(m => m.refId === s.id && m.refType === 'sentence');
    return { label: s.en, sub: s.zh, levels: row?.levels || {} };
  });

  box.innerHTML = card(`
    <div class="unit-head">
      <div>
        <h3 style="margin:0">Unit ${unit.no} ${escapeHtml(unit.title || '')}</h3>
        <p class="muted" style="font-size:12.5px;margin:2px 0 0">${escapeHtml(unit.theme || '')}</p>
      </div>
      <div class="row" style="gap:8px">
        <button class="btn btn-sm btn-primary" data-act="practice">开始练习</button>
        <button class="btn btn-sm btn-ghost" data-act="copy">复制到本地库编辑</button>
        <button class="btn btn-sm btn-danger" data-act="delete">删除导入内容</button>
      </div>
    </div>

    ${unit.objectives?.length ? `<div class="obj-list">${unit.objectives.map(o => `<span class="chip">${escapeHtml(o)}</span>`).join('')}</div>` : ''}

    <div class="tab-bar" data-tabs>
      <button class="tab on" data-tab="words">单词 ${content.words.length}</button>
      <button class="tab" data-tab="sentences">句子 ${content.sentences.length}</button>
      <button class="tab" data-tab="texts">课文 ${content.texts?.length || 0}</button>
      <button class="tab" data-tab="grammar">语法 ${content.grammar.length}</button>
      <button class="tab" data-tab="phonics">语音/词组 ${(content.phonics ? 1 : 0) + (content.phrases?.length || 0) ? '·' : ''} ${(content.phrases?.length || 0)} 组</button>
      <button class="tab" data-tab="questions">题目 ${content.questions.length}</button>
      <button class="tab" data-tab="mastery">掌握矩阵</button>
    </div>

    <div data-panel="words">
      <div class="table-wrap"><table class="tbl">
        <thead><tr><th>单词</th><th>音标</th><th>词性</th><th>中文</th><th>短语</th><th>例句</th><th></th></tr></thead>
        <tbody>${content.words.length ? content.words.map(w => `
          <tr>
            <td class="en"><b>${escapeHtml(w.en)}</b></td>
            <td class="mono muted">${escapeHtml(w.phonetic || '—')}</td>
            <td class="muted">${escapeHtml(w.pos || '—')}</td>
            <td>${escapeHtml(w.zh || '—')}</td>
            <td>${w.phrase ? `${escapeHtml(w.phrase)}<br><small class="muted">${escapeHtml(w.phraseZh || '')}</small>` : '—'}</td>
            <td style="max-width:260px">${w.sentence ? `${escapeHtml(w.sentence)}<br><small class="muted">${escapeHtml(w.sentenceZh || '')}</small>` : '—'}</td>
            <td><button class="icon-btn" data-en="${escapeHtml(w.en)}" data-audio="${escapeHtml(w.audio || '')}" type="button" title="发音">🔊</button></td>
          </tr>`).join('') : '<tr class="empty-row"><td colspan="7">暂无单词</td></tr>'}
        </tbody>
      </table></div>
    </div>

    <div data-panel="sentences" hidden>
      <div class="table-wrap"><table class="tbl">
        <thead><tr><th>英文</th><th>中文</th><th>句型</th><th></th></tr></thead>
        <tbody>${content.sentences.length ? content.sentences.map(s => `
          <tr>
            <td class="en">${escapeHtml(s.en)}</td>
            <td>${escapeHtml(s.zh || '—')}</td>
            <td>${s.pattern ? `<span class="tag">${escapeHtml(s.pattern)}</span>` : '—'}</td>
            <td><button class="icon-btn" data-sentence="${escapeHtml(s.en)}" type="button" title="发音">🔊</button></td>
          </tr>`).join('') : '<tr class="empty-row"><td colspan="4">暂无句子</td></tr>'}
        </tbody>
      </table></div>
    </div>

    <div data-panel="grammar" hidden>
      ${content.grammar.length ? `<div class="grammar-list">
        ${content.grammar.map(g => `
          <div class="grammar-item">
            <div class="row" style="gap:8px;align-items:center">
              <span class="tag blue">${escapeHtml(g.category || '未分类')}</span>
              <b style="font-size:13.5px">${escapeHtml(g.rule || '')}</b>
            </div>
            ${g.examples?.length ? `<ul class="example-list">${g.examples.map(e => `<li>${escapeHtml(e)}</li>`).join('')}</ul>` : ''}
          </div>`).join('')}
      </div>` : '<p class="muted">暂无语法点</p>'}
    </div>

    <div data-panel="questions" hidden>
      <div class="row" style="gap:8px;margin-bottom:10px;align-items:center">
        <span class="muted" style="font-size:12px">共 ${content.questions.length} 题 · 修改立即保存到本地，练习时用最新版本</span>
        <button class="btn btn-sm btn-primary" data-add-q type="button" style="margin-left:auto">➕ 新增题目</button>
      </div>
      <div class="table-wrap"><table class="tbl">
        <thead><tr><th style="width:60px">题型</th><th>题干</th><th style="width:120px">答案</th><th style="width:70px">来源</th><th style="width:150px">操作</th></tr></thead>
        <tbody>${content.questions.length ? content.questions.map(q => `
          <tr>
            <td><span class="tag">${escapeHtml(q.type)}</span></td>
            <td style="max-width:420px">${escapeHtml(String(q.prompt || '').replace(/\n/g, ' '))}</td>
            <td class="en">${escapeHtml((q.answer || []).join(' / '))}</td>
            <td class="muted">${q.origin === 'textbook' ? '教材' : q.origin === 'ocr' ? 'OCR' : '生成'}</td>
            <td>
              <button class="icon-btn" data-edit-q="${q.id}" title="编辑">🖊</button>
              <button class="icon-btn" data-toggle-q="${q.id}" title="${q.status === 'disabled' ? '启用' : '停用'}">${q.status === 'disabled' ? '▶' : '⏸'}</button>
              <button class="icon-btn" data-del-q="${q.id}" title="删除">🗑</button>
            </td>
          </tr>`).join('') : '<tr class="empty-row"><td colspan="5">暂无题目（练习时会自动生成；也可以点上方「新增题目」手工录入）</td></tr>'}
        </tbody>
      </table></div>
    </div>

    <div data-panel="texts" hidden>
      ${content.texts?.length ? content.texts.map(t => `
        <div class="grammar-item" style="margin-bottom:14px">
          <div class="row" style="gap:8px;align-items:center">
            <span class="tag blue">${escapeHtml(t.section || '课文')}</span>
            ${t.title ? `<b style="font-size:13.5px">${escapeHtml(t.title)}</b>` : ''}
            <span class="muted" style="font-size:11.5px">教材 PDF 第 ${t.page} 页</span>
            ${t.needConfirm ? '<span class="tag" style="background:#fff3cd">待确认</span>' : ''}
          </div>
          ${t.note ? `<p class="muted" style="font-size:12px;margin:6px 0 0">⚠ ${escapeHtml(t.note)}</p>` : ''}
          <div style="margin-top:8px">${(t.lines || []).map(l => `
            <p style="margin:4px 0">
              ${l.who ? `<span class="tag">${escapeHtml(l.who)}</span> ` : ''}
              <span class="en">${escapeHtml(l.en)}</span>
              ${l.zh ? `<br><small class="muted">${escapeHtml(l.zh)}</small>` : ''}
            </p>`).join('')}
          </div>
        </div>`).join('') : '<p class="muted">本单元暂无课文数据（教材 PDF 中该部分为扫描图或不在 PDF 覆盖范围内）</p>'}
    </div>

    <div data-panel="phonics" hidden>
      ${content.phonics ? `
        <div class="grammar-item" style="margin-bottom:14px">
          <div class="row" style="gap:8px;align-items:center">
            <span class="tag blue">🔤 字母组合 ${escapeHtml(content.phonics.letters)}</span>
            <b>${escapeHtml(content.phonics.sound || '')}</b>
            <span class="muted" style="font-size:11.5px">教材 PDF 第 ${content.phonics.page} 页</span>
          </div>
          ${content.phonics.tip ? `<p style="font-size:13px;margin:6px 0 0">${escapeHtml(content.phonics.tip)}</p>` : ''}
          <div class="row mt-8" style="gap:6px;flex-wrap:wrap">
            ${(content.phonics.words || []).map(w => `
              <span class="chip" title="${w.needConfirm ? '待确认：' + (w.note || '') : ''}">
                ${escapeHtml(w.en)}${w.needConfirm ? ' ⚠' : ''}
              </span>`).join('')}
          </div>
        </div>` : '<p class="muted">本单元暂无 Phonics 数据</p>'}
      ${content.phrases?.length ? `
        <h4 style="margin:16px 0 8px">重点词组（${content.phrases.length}）</h4>
        <div class="table-wrap"><table class="tbl">
          <thead><tr><th>词组</th><th>中文</th><th>来源页</th></tr></thead>
          <tbody>${content.phrases.map(p => `
            <tr>
              <td class="en"><b>${escapeHtml(p.en)}</b></td>
              <td>${escapeHtml(p.zh || '—')}${p.needConfirm ? ' <span class="tag" style="background:#fff3cd">待确认</span>' : ''}</td>
              <td class="muted">${p.srcPage ? '教材 PDF p' + p.srcPage : '—'}</td>
            </tr>`).join('')}
        </tbody></table></div>` : '<p class="muted mt-8">本单元暂无词组数据</p>'}
    </div>

    <div data-panel="mastery" hidden>
      <h4 style="margin:16px 0 8px">单词 · 7 级掌握情况</h4>
      ${masteryMatrix(WORD_LEVELS, wordRows, { emptyText: '还没有单词练习记录' })}
      <h4 style="margin:20px 0 8px">句子 · 8 级掌握情况</h4>
      ${masteryMatrix(SENTENCE_LEVELS, sentRows, { emptyText: '还没有句子练习记录' })}
    </div>
  `, {});

  /* Tab 切换 */
  const tabs = box.querySelector('[data-tabs]');
  tabs?.querySelectorAll('.tab').forEach(t => {
    t.addEventListener('click', () => {
      tabs.querySelectorAll('.tab').forEach(x => x.classList.toggle('on', x === t));
      box.querySelectorAll('[data-panel]').forEach(p => {
        p.hidden = p.dataset.panel !== t.dataset.tab;
      });
    });
  });


  /* 题库编辑：新增 / 编辑 / 停用 / 删除 */
  const tbId = state.selection.textbookId;
  const uId = state.selection.unitId;

  function openQuestionModal(q, onSave) {
    const isNew = !q;
    const types = ['choice','listen','fill','spell','judge','order','transform'];
    const typeOpts = types.map(t => `<option value="${t}" ${q?.type===t?'selected':''}>${t}</option>`).join('');
    const m = openModal({
      title: isNew ? '➕ 新增题目' : '🖊 编辑题目',
      size: 'wide',
      body: `
        <div class="form-grid">
          <label>题型 <select class="input" data-f="type">${typeOpts}</select></label>
          <label>难度 <input type="number" class="input" data-f="level" min="1" max="6" value="${q?.level || 1}"></label>
          <label>题干 <textarea class="input" data-f="prompt" rows="3">${escapeHtml(q?.prompt || '')}</textarea></label>
          <label>选项（每行一个，选择题型用）<textarea class="input" data-f="options" rows="3">${escapeHtml((q?.options || []).join('\n'))}</textarea></label>
          <label>答案（多个用 / 分隔）<input class="input" data-f="answer" value="${escapeHtml((q?.answer || []).join(' / '))}"></label>
          <label>知识点 <input class="input" data-f="grammarCategory" value="${escapeHtml(q?.grammarCategory || '')}"></label>
          <label>音频路径（选填，内置 MP3）<input class="input" data-f="audioUrl" value="${escapeHtml(q?.audioUrl || '')}" placeholder="audio/u2/strong.mp3"></label>
          <label>音频文本（没 MP3 时 TTS 兜底）<input class="input" data-f="audio" value="${escapeHtml(q?.audio || '')}" placeholder="英文单词或句子"></label>
          <label>解析 <textarea class="input" data-f="explain" rows="2">${escapeHtml(q?.explain || '')}</textarea></label>
          <label>提示 <input class="input" data-f="hint" value="${escapeHtml(q?.hint || '')}"></label>
        </div>`,
      buttons: [{ text: '取消', kind: 'ghost' }, { text: '保存', kind: 'primary', id: 'save-q' }],
    });
    m.querySelector('#save-q').onclick = async () => {
      const read = f => m.querySelector(`[data-f="${f}"]`).value.trim();
      const data = {
        type: read('type'), level: Number(read('level')),
        prompt: read('prompt'),
        options: read('options').split('\n').filter(Boolean),
        answer: read('answer').split(/[\/、]/).map(s => s.trim()).filter(Boolean),
        grammarCategory: read('grammarCategory'),
        audioUrl: read('audioUrl'), audio: read('audio'),
        explain: read('explain'), hint: read('hint'),
      };
      onSave(data);
    };
  }

  box.querySelectorAll('[data-add-q]').forEach(btn => btn.addEventListener('click', async () => {
    openQuestionModal(null, async (d) => {
      const storage = getStorage();
      const newQ = makeQuestion({ ...d, textbookId: tbId, unitId: uId, origin: 'manual', status: 'confirmed' });
      await storage.put(COLLECTIONS.QUESTIONS, newQ);
      invalidateCache(tbId, uId);
      emit(EVENTS.CONTENT_CHANGED);
      toast('✅ 题目已新增', 'success');
      closeModal();
      navigate(`#/textbooks/${tbId}`);
    });
  }));

  box.querySelectorAll('[data-edit-q]').forEach(btn => btn.addEventListener('click', async () => {
    const storage = getStorage();
    const fresh = await getUnitContent(tbId, uId);
    const q = fresh.questions.find(x => x.id === btn.dataset.editQ);
    if (!q) return toast('找不到题目', 'warn');
    openQuestionModal(q, async (d) => {
      const merged = { ...q, ...d, status: 'confirmed' };
      await storage.put(COLLECTIONS.QUESTIONS, merged);
      invalidateCache(tbId, uId);
      emit(EVENTS.CONTENT_CHANGED);
      toast('✅ 保存成功，练习时立即生效', 'success');
      closeModal();
      navigate(`#/textbooks/${tbId}`);
    });
  }));

  box.querySelectorAll('[data-toggle-q]').forEach(btn => btn.addEventListener('click', async () => {
    const storage = getStorage();
    const fresh = await getUnitContent(tbId, uId);
    const q = fresh.questions.find(x => x.id === btn.dataset.toggleQ);
    if (!q) return;
    q.status = q.status === 'disabled' ? 'confirmed' : 'disabled';
    await storage.put(COLLECTIONS.QUESTIONS, q);
    invalidateCache(tbId, uId);
    emit(EVENTS.CONTENT_CHANGED);
    toast(q.status === 'disabled' ? '已停用（练习不再出现）' : '已启用（练习可见）');
    navigate(`#/textbooks/${tbId}`);
  }));

  box.querySelectorAll('[data-del-q]').forEach(btn => btn.addEventListener('click', async () => {
    const ok = await confirmDialog({ title: '删除这道题？', message: '删除后可在导入中心重建。' });
    if (!ok) return;
    const storage = getStorage();
    await storage.remove(COLLECTIONS.QUESTIONS, btn.dataset.delQ);
    invalidateCache(tbId, uId);
    emit(EVENTS.CONTENT_CHANGED);
    toast('已删除');
    navigate(`#/textbooks/${tbId}`);
  }));


    /* 发音：单词优先走内置 MP3（playWord），句子走 playSentenceEn，都没有才 fallback autoSpeak */
  box.querySelectorAll('[data-en], [data-sentence], [data-speak]').forEach(b => {
    b.addEventListener('click', () => {
      const wordText = b.dataset.en;
      const audioUrl = b.dataset.audio;
      const sentence = b.dataset.sentence;
      const legacyText = b.dataset.speak;   // 兼容旧写法
      if (wordText && audioUrl) {
        playWord({ en: wordText, audio: audioUrl });
      } else if (wordText) {
        playWord({ en: wordText });          // 没 audio 也能走 TTS 兜底
      } else if (sentence) {
        playSentenceEn(sentence);
      } else if (legacyText) {
        autoSpeak(legacyText);               // 最后兜底
      }
    });
  });

  /* 操作 */
  box.querySelector('[data-act="practice"]')?.addEventListener('click', async () => {
    await saveSelection({ textbookId: book.id, unitId: unit.id });
    navigate('#/practice', { module: 'word' });
  });

  box.querySelector('[data-act="copy"]')?.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: '复制到本地库',
      message: `把 Unit ${unit.no} 的内置内容复制一份到本地存储，之后就可以在导入中心修改它。原内置文件不会被改动。`,
      okText: '复制',
    });
    if (!ok) return;
    const draft = await packToDraft(content, { textbookId: book.id, sourceName: `${book.name} · Unit ${unit.no}` });
    draft.unit.no = unit.no;
    draft.unit.title = unit.title;
    await saveDraft(draft);
    toast('已生成可编辑草稿，请到导入中心查看', 'success');
    navigate('#/import');
  });

  box.querySelector('[data-act="delete"]')?.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: '删除导入内容',
      message: '只会删除从导入中心写入本单元的内容，内置数据文件不受影响。学习记录会保留。',
      okText: '删除',
      danger: true,
    });
    if (!ok) return;
    await deleteUnitContent(book.id, unit.id);
    invalidateCache(book.id, unit.id);
    emit(EVENTS.CONTENT_CHANGED, { textbookId: book.id, unitId: unit.id });
    toast('已删除本单元的导入内容', 'success');
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  });
}


/* ------------------------------ 资料库区块 ------------------------------ */
/**
 * 展示 data/raw/ 下已经进入项目的 PDF：
 * 文件名 / 类型 / 是否已识别 / 是否已确认 / 为什么没识别出来。
 * 明确显示失败原因，是因为「让你反复重传」是最糟的体验。
 */
async function renderLibrary(mount) {
  const host = mount.querySelector('#library-block');
  if (!host) return;
  let lib;
  try {
    lib = await loadLibrary();
  } catch (err) {
    host.innerHTML = card(`<p class="muted">资料库清单读取失败：${escapeHtml(err.message)}</p>`, { title: '📥 资料库' });
    return;
  }
  if (!lib.items.length) {
    host.innerHTML = card(emptyState('📭', '资料库还是空的', '在导入中心上传 PDF 后会出现在这里'), { title: '📥 资料库' });
    return;
  }

  const tb = lib.items.filter(i => i.type === 'textbook');
  const ex = lib.items.filter(i => i.type === 'exercise');

  const row = it => {
    const recognized = it.recognized
      ? tag('已识别', 'green')
      : (it.ocr?.done ? tag('OCR 完成', 'blue') : tag('未识别', 'red'));
    const confirmed = it.confirmed ? tag('已确认', 'green') : tag('待确认', '');
    return `<div class="lib-row">
      <div class="lib-main">
        <div class="lib-title">${it.type === 'textbook' ? '📘' : '📝'} ${escapeHtml(it.title)}</div>
        <div class="lib-file mono">${escapeHtml(it.path)}</div>
        <div class="lib-meta">
          <span>${it.pages} 页</span>
          <span>${it.sizeMB} MB</span>
          <span>${it.hasTextLayer ? `有文字层（${it.textChars} 字符）` : '扫描图片，无文字层'}</span>
        </div>
        ${it.ocr?.done ? `<div class="lib-sum">🔍 OCR：${it.ocr.pages} 页全部完成（${escapeHtml(it.ocr.method || '')}），
          逐页原文在 <code class="mono">data/${escapeHtml(it.ocr.dir)}/page-NN.txt</code>，页码与原 PDF 一一对应${it.ocr.failedPages?.length ? `，失败页：${it.ocr.failedPages.join(',')}` : ''}。</div>` : ''}
        ${it.blockedReason ? `<div class="lib-block">⚠ ${escapeHtml(it.blockedReason)}</div>` : ''}
        ${it.summary ? `<div class="lib-sum">${escapeHtml(it.summary)}</div>` : ''}
      </div>
      <div class="lib-side">
        ${recognized}
        ${confirmed}
        ${it.recognized && !it.confirmed
          ? `<button class="btn btn-sm btn-primary" data-confirm="${it.id}" type="button">确认入库</button>` : ''}
      </div>
    </div>`;
  };

  host.innerHTML = card(`
    <p class="muted" style="font-size:12.5px;margin-bottom:12px">
      以下 PDF 是<b>你之前已经上传的原始文件</b>，已复制进项目 <code class="mono">data/raw/</code> 目录，不会再要求你重新上传。
      教材和练习卷分开存放：<code class="mono">data/raw/textbooks/</code> 与 <code class="mono">data/raw/exercises/</code>。
    </p>
    <div class="lib-group-title">教材 PDF（${tb.length}）</div>
    ${tb.map(row).join('') || '<p class="muted small">无</p>'}
    <div class="lib-group-title">练习/试卷 PDF（${ex.length}）</div>
    ${ex.map(row).join('') || '<p class="muted small">无</p>'}
    <p class="muted mt-16" style="font-size:12px">
      状态说明：<b>已识别</b> = 程序从 PDF 里取到了文字并转成结构化数据；
      <b>OCR 完成</b> = 扫描图片已用 Tesseract 转成逐页文本（保留页码），但题目尚未人工录入/确认；
      <b>未识别</b> = 既没有文字层也没有 OCR 结果。所有状态仅供参考，最终以你人工确认为准。
    </p>`, { title: '📥 资料库（已导入的 PDF）', extra: tag(`${lib.items.length} 份文件`, 'blue') });

  host.querySelectorAll('[data-confirm]').forEach(btn => {
    btn.addEventListener('click', async () => {
      await markConfirmed(btn.dataset.confirm);
      toast('已确认，状态已保存到本机', 'success');
      renderLibrary(mount);
    });
  });
}
